import React, { useState } from "react";
import { Box, Flex, Link } from "rebass/styled-components";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { HiChevronDown, HiX } from "react-icons/hi";
import { Body, Subheader } from "MVVM/Components/Text";
import { analytics } from "MVVM/Analytics";
import type { TrayEntry } from "../../Hooks/useClipStatusRegistry";
import type { TVodTrayMeta } from "../../Hooks/useVodTrayMeta";
import { matchVodTrayUrl, stripVodSuffix } from "../../Hooks/useVodTrayMeta";
import type { ClipStatusTimeline } from "../../@types";
import { OverallState } from "../../@types";

type VodEntry = Extract<TrayEntry, { kind: "vod" }>;

type MatchVodsCardProps = {
  entries: VodEntry[];
  metaById: Record<string, TVodTrayMeta>;
  onDismissAll: () => void;
};

const isDone = (t: ClipStatusTimeline) =>
  t.overallState === OverallState.COMPLETED ||
  t.overallState === OverallState.COMPLETED_DESKTOP;

const rowStatus = (timelines: ClipStatusTimeline[]) => {
  const done = timelines.filter(isDone).length;
  const errored = timelines.some(
    (t) => t.overallState === OverallState.ERRORED,
  );
  if (errored) return { text: "Error", state: "error" as const };
  if (done === timelines.length)
    return { text: "Ready", state: "done" as const };
  return {
    text: `${done}/${timelines.length} rendered`,
    state: "progress" as const,
  };
};

export const MatchVodsCard = ({
  entries,
  metaById,
  onDismissAll,
}: MatchVodsCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const ready = entries.filter((e) => rowStatus(e.timelines).state === "done");
  const summary =
    ready.length === entries.length
      ? "All ready"
      : `${entries.length - ready.length} processing`;

  const handleToggle = () => {
    if (!expanded) {
      analytics.track("Clip Status Card Expanded", {
        contentType: "full_match_vod_group",
        vodCount: entries.length,
      });
    }
    setExpanded((prev) => !prev);
  };

  return (
    <CardBox role="status" aria-live="polite">
      <HeaderRow onClick={handleToggle}>
        <Flex flex={1} flexDirection="column">
          <CardTitle>Match VODs ({entries.length})</CardTitle>
          <SummaryText>{summary}</SummaryText>
        </Flex>
        <ChevronWrap $expanded={expanded}>
          <HiChevronDown size={16} />
        </ChevronWrap>
        <DismissButton
          aria-label="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismissAll();
          }}
        >
          <HiX size={14} />
        </DismissButton>
      </HeaderRow>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            <VodList>
              {entries.map((entry) => {
                const meta = metaById[entry.montageId];
                const status = rowStatus(entry.timelines);
                const url = matchVodTrayUrl(meta);

                return (
                  <VodRow key={entry.montageId}>
                    <VodTitle>
                      {stripVodSuffix(meta?.title) || "Match VOD"}
                    </VodTitle>
                    <VodStatus $state={status.state}>{status.text}</VodStatus>
                    {status.state === "done" && url && (
                      <ViewLink
                        href={url}
                        onClick={() =>
                          analytics.track("Clip Status CTA Clicked", {
                            montageId: entry.montageId,
                            action: "watch",
                            contentType: "FULL_MATCH_VOD",
                          })
                        }
                      >
                        View
                      </ViewLink>
                    )}
                  </VodRow>
                );
              })}
            </VodList>
          </motion.div>
        )}
      </AnimatePresence>
    </CardBox>
  );
};

const CardBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  padding: 12px 16px;
  width: 320px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    width: 100%;
  }
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  cursor: pointer;
  gap: 8px;
  user-select: none;
`;

const CardTitle = styled(Body)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12px;
  font-weight: 600;
`;

const SummaryText = styled(Subheader)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
`;

const ChevronWrap = styled(Flex)<{ $expanded: boolean }>`
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
  transition: transform 0.2s ease;
`;

const DismissButton = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const VodList = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  padding: 10px 0 2px;
`;

const VodRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const VodTitle = styled(Subheader)`
  color: ${({ theme }) => theme.colors.chalk};
  flex: 1;
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VodStatus = styled(Subheader)<{
  $state: "done" | "error" | "progress";
}>`
  color: ${({ $state, theme }) =>
    $state === "error"
      ? theme.colors.alert
      : $state === "done"
      ? theme.colors.envy
      : theme.colors.chalk};
  flex-shrink: 0;
  font-size: 11px;
`;

const ViewLink = styled(Link)`
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;
